import { isDefined } from "@clipboard-health/util-ts";
import { useInfiniteGetQuery, type UseInfiniteGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseInfiniteQueryResult } from "@tanstack/react-query";
import { z } from "zod";

import { RequirementWorkflowType } from "../constants";
import {
  requirementIncludeSchema,
  type RequirementListResponse,
  requirementListResponseSchema,
} from "../types";

const getRequirementsRequestFilterDtoSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  requirementWorkflowType: z.nativeEnum(RequirementWorkflowType).optional(),
  archived: z.boolean().optional(),
});

export type GetRequirementsRequestFilterDto = z.infer<typeof getRequirementsRequestFilterDtoSchema>;

const getRequirementsRequestSchema = z.object({
  filter: getRequirementsRequestFilterDtoSchema.optional(),
  include: requirementIncludeSchema,
  page: z
    .object({
      size: z.number().int().positive().optional(),
      cursor: z.string().optional(),
    })
    .optional(),
});

export type GetRequirementsRequestDto = z.infer<typeof getRequirementsRequestSchema>;

export interface UseGetRequirementsOptions extends UseInfiniteGetQueryOptions<RequirementListResponse> {
  customHeaders?: Record<string, string>;
}

const PAGE_SIZE = 20;

export function useGetRequirements(
  params: GetRequirementsRequestDto = {},
  options: UseGetRequirementsOptions = {}
): UseInfiniteQueryResult<RequirementListResponse> {
  const { customHeaders, ...queryOptions } = options;

  return useInfiniteGetQuery({
    url: `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/requirements/`,
    queryParams: {
      ...params,
      page: {
        ...(isDefined(params.page) && params.page),
        size: params.page?.size ?? PAGE_SIZE,
      },
    },
    requestSchema: getRequirementsRequestSchema,
    responseSchema: requirementListResponseSchema,
    customHeaders,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_REQUIREMENTS_FAILURE,
    },
    getNextPageParam: (lastPage) =>
      lastPage.links?.nextCursor
        ? { page: { cursor: lastPage.links.nextCursor, size: params.page?.size ?? PAGE_SIZE } }
        : undefined,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });
}
